import React, { useEffect, useState } from 'react';
import { useNavigate } from '../i18n/router';
import { useNavigate as useRouterNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, {
  LANG_STORAGE_KEY,
  type SupportedLanguage,
} from '../i18n';
import { localizedPath } from '../i18n/router';
import { LANGBAR_HEIGHT } from '../components/BTLangBar';
import {
  Box,
  Button,
  Typography,
  Modal,
  Drawer,
  IconButton,
  Divider,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';

import {
  MdHelpOutline,
  MdChevronRight,
  MdInfoOutline,
  MdQuestionAnswer,
  MdReceipt,
  MdClose,
  MdLogout,
  MdDeleteForever,
  MdLanguage,
} from 'react-icons/md';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { deleteAccount, recupereListeFactures } from '../Services';
interface CarouselItem {
  url: string;
}

interface ParameterScreenProps {
  open: boolean;
  onClose: () => void;
}

const ParameterScreen: React.FC<ParameterScreenProps> = ({ open, onClose }) => {
  const [userName, setUserName] = useState<string>('');
  const [userMail, setUserMail] = useState<string>('');
  const [factures, setFactures] = useState<string>('');
  const [, setVisas] = useState<string[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);

  const carouselItems: CarouselItem[] = hideFees
    ? [
        { url: 'https://betravel-app.visamonde.com/tuto/1.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/2.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/4.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/5.png' },
      ]
    : [
        { url: 'https://betravel-app.visamonde.com/tuto/1.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/2.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/3.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/4.png' },
        { url: 'https://betravel-app.visamonde.com/tuto/5.png' },
      ];

  const navigate = useNavigate();
  const routerNavigate = useRouterNavigate();
  const location = useLocation();
  const { t, i18n: i18nInstance } = useTranslation(['i18n', 'params', 'common']);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const currentLang = (i18nInstance.language || 'fr') as SupportedLanguage;

  const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchor(event.currentTarget);
  };
  const handleCloseLangMenu = () => setLangAnchor(null);

  const handleSwitchLanguage = (lang: SupportedLanguage) => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch { /* ignore */ }
    void i18n.changeLanguage(lang);
    const target = localizedPath(location.pathname, lang) + location.search + location.hash;
    routerNavigate(target, { replace: false });
    setLangAnchor(null);
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          setUserName(user.nom);
          setUserMail(user.email);
          console.log('Appel de recupereListeFactures avec email:', user.email);
          const facturesHtml = await recupereListeFactures(user.email);
          console.log('Factures reçues:', facturesHtml);
          setFactures(facturesHtml);
        }
      } catch (error) {
        console.error('Failed to load user.', error);
      }
    };

    const loadVisas = async () => {
      try {
        setVisas([]);
      } catch (error) {
        console.error('Failed to load visas.', error);
      }
    };

    loadUser();
    loadVisas();
  }, []);

  const handleLogout = async () => {
    try {
      localStorage.removeItem('user');
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout.', error);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmDelete = window.confirm(t('params:delete.confirm'));
    if (!confirmDelete) return;

    try {
      const storedMail = localStorage.getItem('mail');
      if (storedMail) {
        await deleteAccount(storedMail);
        localStorage.removeItem('user');
        localStorage.removeItem('mail');
        alert(t('params:delete.success'));
        navigate('/login');
      } else {
        alert(t('params:delete.noUser'));
      }
    } catch (error) {
      console.error('Failed to delete account.', error);
      alert(t('params:delete.failed'));
    }
  };

  const closePdf = () => {
    setSelectedPdf(null);
  };

  const handleTutorialPress = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleContactPress = () => {
    window.open('https://betravel-app.visamonde.com/#contact', '_blank');
  };

  const handleFaqPress = () => {
    window.open('https://betravel-app.visamonde.com/FAQ.html', '_blank');
  };

  const toggleDrawer = (openState: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setIsDrawerOpen(openState);
  };

  // Get user initials for avatar fallback
  const initials = userName ? userName.charAt(0).toUpperCase() : '?';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0,0,0,0.3)' },
        },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '85%', sm: '380px', md: '400px' },
          backgroundColor: '#fafbfc',
          boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
          borderRadius: { xs: '16px 0 0 16px', sm: '20px 0 0 20px' },
          top: `${LANGBAR_HEIGHT}px`,
          height: `calc(100vh - ${LANGBAR_HEIGHT}px)`,
        },
      }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Close button */}
        {!selectedPdf && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
              width: 40,
              height: 40,
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: '#555' }} />
          </IconButton>
        )}

        {selectedPdf ? (
          /* PDF Viewer */
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <iframe
              src={selectedPdf}
              title="PDF Viewer"
              style={{ width: '100%', flex: 1, border: 'none' }}
            />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={closePdf}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #333 0%, #555 100%)',
                }}
              >
                {t('common:close')}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* User Profile Header */}
            <Box
              sx={{
                pt: 5,
                pb: 3,
                px: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                borderRadius: '0 0 24px 24px',
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  mx: 'auto',
                  mb: 1.5,
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '3px solid rgba(255,255,255,0.3)',
                }}
              >
                {initials}
              </Avatar>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: 'white', fontSize: '1.15rem' }}
              >
                {t('params:greeting', { name: userName })}
              </Typography>
              {userMail && (
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5, fontSize: '0.8rem' }}
                >
                  {userMail}
                </Typography>
              )}
            </Box>

            {/* Menu Items */}
            <Box sx={{ flex: 1, px: 2, pt: 3 }}>
              <List disablePadding>
                {/* Factures */}
                {!hideFees && (
                  <ListItemButton
                    onClick={toggleDrawer(true)}
                    sx={{
                      borderRadius: 3,
                      mb: 1,
                      backgroundColor: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <MdReceipt size={22} color="#2e7d32" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('params:menu.viewInvoices')}
                      primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem', color: '#333' }}
                    />
                    <MdChevronRight size={20} color="#bbb" />
                  </ListItemButton>
                )}

                {/* Langue */}
                <ListItemButton
                  onClick={handleOpenLangMenu}
                  sx={{
                    borderRadius: 3,
                    mb: 1,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MdLanguage size={22} color="#2e7d32" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('languageLabel')}
                    secondary={t(`languages.${currentLang}`)}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem', color: '#333' }}
                    secondaryTypographyProps={{ fontSize: '0.8rem', color: '#888' }}
                  />
                  <MdChevronRight size={20} color="#bbb" />
                </ListItemButton>
                <Menu
                  anchorEl={langAnchor}
                  open={Boolean(langAnchor)}
                  onClose={handleCloseLangMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
                >
                  {(['fr', 'en', 'nl', 'de'] as const).map((lang) => (
                    <MenuItem
                      key={lang}
                      selected={lang === currentLang}
                      onClick={() => handleSwitchLanguage(lang)}
                      sx={{ gap: 1.5, py: 1.2 }}
                    >
                      <Box
                        component="img"
                        src={`https://flagcdn.com/24x18/${lang === 'en' ? 'gb' : lang}.png`}
                        alt={lang}
                        sx={{ width: 22, height: 16, borderRadius: '2px', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: lang === currentLang ? 600 : 400 }}>
                        {t(`languages.${lang}`)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>

                {/* Tutoriel */}
                <ListItemButton
                  onClick={handleTutorialPress}
                  sx={{
                    borderRadius: 3,
                    mb: 1,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MdHelpOutline size={22} color="#2e7d32" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('params:menu.viewTutorial')}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem', color: '#333' }}
                  />
                  <MdChevronRight size={20} color="#bbb" />
                </ListItemButton>

                {/* Contact */}
                <ListItemButton
                  onClick={handleContactPress}
                  sx={{
                    borderRadius: 3,
                    mb: 1,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MdInfoOutline size={22} color="#2e7d32" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('params:menu.contact')}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem', color: '#333' }}
                  />
                  <MdChevronRight size={20} color="#bbb" />
                </ListItemButton>

                {/* FAQ */}
                <ListItemButton
                  onClick={handleFaqPress}
                  sx={{
                    borderRadius: 3,
                    mb: 1,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MdQuestionAnswer size={22} color="#2e7d32" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('params:menu.faq')}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem', color: '#333' }}
                  />
                  <MdChevronRight size={20} color="#bbb" />
                </ListItemButton>
              </List>
            </Box>

            {/* Footer Actions */}
            <Box sx={{ px: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Divider sx={{ mb: 1, opacity: 0.5 }} />

              <Button
                variant="contained"
                fullWidth
                startIcon={<MdLogout size={18} />}
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  background: 'linear-gradient(135deg, #333 0%, #555 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #222 0%, #444 100%)',
                  },
                }}
              >
                {t('params:actions.logout')}
              </Button>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<MdDeleteForever size={18} />}
                onClick={handleDeleteAccount}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderColor: '#e53935',
                  color: '#e53935',
                  '&:hover': {
                    borderColor: '#c62828',
                    backgroundColor: 'rgba(229,57,53,0.04)',
                  },
                }}
              >
                {t('params:actions.deleteAccount')}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Drawer pour les factures */}
      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '80vh',
            boxShadow: '0 -8px 30px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Handle bar */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: '#ddd',
              mx: 'auto',
              mb: 2,
            }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {t('params:invoices.title')}
            </Typography>
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.06)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
                width: 36,
                height: 36,
              }}
            >
              <MdClose size={18} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {factures ? (
              <div dangerouslySetInnerHTML={{ __html: factures }} />
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <MdReceipt size={48} color="#ccc" />
                <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                  {t('params:invoices.empty')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Modal tutoriel carousel */}
      <CarouselModal
        open={isModalVisible}
        onClose={handleModalClose}
        items={carouselItems}
      />
    </Drawer>
  );
};

/* ------------------------------------------------------------------ */
/* Carousel Modal                                                     */
/* ------------------------------------------------------------------ */
interface CarouselModalProps {
  open: boolean;
  onClose: () => void;
  items: CarouselItem[];
}

const CarouselModal: React.FC<CarouselModalProps> = ({ open, onClose, items }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: { xs: 2, sm: 4 },
          outline: 'none',
          borderRadius: 4,
          width: { xs: '92%', sm: '70%', md: '50%' },
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: 'rgba(0,0,0,0.06)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
            width: 36,
            height: 36,
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Image */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Box
            component="img"
            src={items[currentIndex].url}
            alt={`Slide ${currentIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '55vh',
              borderRadius: 2,
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Dots indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8, mb: 2 }}>
          {items.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: currentIndex === idx ? 20 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: currentIndex === idx ? '#2e7d32' : '#ddd',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentIndex(idx)}
            />
          ))}
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton
            onClick={handlePrev}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#eee' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Typography variant="caption" sx={{ color: '#999' }}>
            {currentIndex + 1} / {items.length}
          </Typography>

          <IconButton
            onClick={handleNext}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#eee' },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ParameterScreen;
